/**
 * Shopping — lists of what you mean to buy.
 *
 * The one screen here that is about the future. Everything else in this app
 * records what happened; this records an intention, and the two must not blur:
 * nothing on a list is money, nothing here moves a bucket, and a list you never
 * shop costs the budget nothing.
 *
 * Adding a line suggests from the catalogue but never insists on it. A name the
 * catalogue knows arrives with its category attached, which is what lets the
 * finished list become an expense with no questions asked; a name it does not
 * know is still a perfectly good line, and becomes a catalogue item the first
 * time you actually buy it.
 *
 * Ticking is not deleting. A ticked line stays, greyed, until you either turn
 * the shop into an expense or clear the ticks — because "did I get the milk?"
 * is a question you ask in the shop, and a list that erases the answer cannot
 * be re-read.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { Product, ShoppingList, ShoppingListItem } from '@et/contracts';
import { api, type Identity } from './api.js';

interface Props {
  identity: Identity;
  products: Product[];
  canEdit: boolean;
  /** Hands the ticked lines to the Add screen as a prefilled draft. */
  onShopDone: (list: ShoppingList, bought: ShoppingListItem[]) => void;
}

export function Shopping({ identity, products, canEdit, onShopDone }: Props) {
  const [lists, setLists] = useState<ShoppingList[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newList, setNewList] = useState('');
  const [draft, setDraft] = useState('');
  const [draftQty, setDraftQty] = useState('1');

  useEffect(() => {
    let cancelled = false;
    api
      .shoppingLists(identity)
      .then((next) => {
        if (cancelled) return;
        setLists(next);
        setOpenId((current) => current ?? next[0]?.id ?? null);
      })
      .catch((e: unknown) => !cancelled && setError(messageOf(e)));
    return () => {
      cancelled = true;
    };
  }, [identity]);

  const open = lists?.find((l) => l.id === openId) ?? null;

  /** The catalogue, minus what is already on this list and minus income. */
  const suggestions = useMemo(() => {
    const typed = draft.trim().toLowerCase();
    if (!typed || !open) return [];
    const already = new Set(open.items.map((i) => i.name.toLowerCase()));
    return products
      .filter((p) => p.bucket !== 'INCOME')
      .filter((p) => p.name.toLowerCase().includes(typed) && !already.has(p.name.toLowerCase()))
      .slice(0, 6);
  }, [draft, open, products]);

  async function run(action: () => Promise<ShoppingList[]>) {
    setBusy(true);
    setError(null);
    try {
      setLists(await action());
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function addList(event: FormEvent) {
    event.preventDefault();
    const name = newList.trim();
    if (!name) return;
    setNewList('');
    setBusy(true);
    setError(null);
    try {
      const next = await api.createShoppingList(identity, { name });
      setLists(next);
      // Open what you just made: creating a list and then having to find it is
      // one click too many.
      setOpenId(next.find((l) => l.name === name)?.id ?? openId);
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  async function addItem(event: FormEvent, productId?: string, nameOverride?: string) {
    event.preventDefault();
    const name = (nameOverride ?? draft).trim();
    if (!name || !open) return;
    setDraft('');
    setDraftQty('1');
    await run(() =>
      api.addShoppingListItem(identity, open.id, {
        name,
        ...(productId ? { productId } : {}),
        quantity: draftQty.trim() || '1',
      }),
    );
  }

  const bought = open?.items.filter((i) => i.checkedAt) ?? [];

  if (!lists) return <p className="hint">Loading…</p>;

  return (
    <section className="shopping">
      <div className="shopping-lists">
        {lists.map((list) => (
          <button
            key={list.id}
            type="button"
            className={list.id === openId ? 'chip chip-on' : 'chip'}
            onClick={() => setOpenId(list.id)}
            aria-pressed={list.id === openId}
          >
            {list.name}
            <span className="list-count">
              {list.items.filter((i) => !i.checkedAt).length}
            </span>
          </button>
        ))}

        {canEdit && (
          <form className="new-list" onSubmit={addList}>
            <input
              value={newList}
              onChange={(e) => setNewList(e.target.value)}
              placeholder="New list — “Weekly shopping”"
              aria-label="New list name"
            />
            <button type="submit" className="chip" disabled={!newList.trim() || busy}>
              Add list
            </button>
          </form>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      {!open && (
        <p className="hint">
          No lists yet. Make one — “Weekly shopping”, “Bi-weekly”, “Hardware shop” — and put
          things on it as you think of them.
        </p>
      )}

      {open && (
        <div className="shopping-open">
          <div className="shopping-head">
            <h3>{open.name}</h3>
            {canEdit && (
              <div className="shopping-actions">
                {bought.length > 0 && (
                  <>
                    <button
                      type="button"
                      className="chip chip-on"
                      onClick={() => onShopDone(open, bought)}
                    >
                      Add {bought.length} as expense
                    </button>
                    <button
                      type="button"
                      className="chip chip-ghost"
                      onClick={() => void run(() => api.clearShoppingListChecked(identity, open.id))}
                      disabled={busy}
                    >
                      Untick all
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="chip chip-ghost"
                  onClick={() => {
                    setOpenId(lists.find((l) => l.id !== open.id)?.id ?? null);
                    void run(() => api.deleteShoppingList(identity, open.id));
                  }}
                  disabled={busy}
                >
                  Delete list
                </button>
              </div>
            )}
          </div>

          {canEdit && (
            <form className="shopping-add" onSubmit={(e) => void addItem(e)}>
              <input
                className="shopping-name"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="What do you need?"
                aria-label="Add to this list"
                autoComplete="off"
              />
              <input
                className="line-qty"
                inputMode="decimal"
                value={draftQty}
                onChange={(e) => setDraftQty(e.target.value)}
                aria-label="How many"
              />
              <button type="submit" className="chip" disabled={!draft.trim() || busy}>
                Add
              </button>
            </form>
          )}

          {suggestions.length > 0 && (
            <div className="suggest" role="listbox" aria-label="Things you have bought">
              {suggestions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="chip suggest-chip"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => void addItem(e, p.id, p.name)}
                >
                  {p.bucket && <span className={`dot dot-${p.bucket.toLowerCase()}`} aria-hidden="true" />}
                  {p.name}
                </button>
              ))}
            </div>
          )}

          {open.items.length === 0 ? (
            <p className="hint">Nothing on this list yet.</p>
          ) : (
            <ul className="shopping-items">
              {open.items.map((item) => (
                <li key={item.id} className={item.checkedAt ? 'got' : ''}>
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(item.checkedAt)}
                      disabled={!canEdit || busy}
                      onChange={(e) =>
                        void run(() =>
                          api.updateShoppingListItem(identity, item.id, {
                            checked: e.target.checked,
                          }),
                        )
                      }
                    />
                    <span className="shopping-item-name">
                      {item.name}
                      {item.quantity !== '1' && <em> ×{item.quantity}</em>}
                    </span>
                  </label>

                  <span className="shopping-item-meta">
                    {item.categoryName ? (
                      <>
                        <span
                          className={`dot dot-${(item.bucket ?? 'needs').toLowerCase()}`}
                          aria-hidden="true"
                        />
                        {item.categoryName}
                      </>
                    ) : (
                      /* Not a warning. It only means the catalogue has not met
                         this yet, which it will the first time you buy it. */
                      <em className="unfiled">new</em>
                    )}
                  </span>

                  {canEdit && (
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => void run(() => api.removeShoppingListItem(identity, item.id))}
                      aria-label={`Remove ${item.name}`}
                      disabled={busy}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong.';
}
